import React, { useState } from "react";
import { useError } from "../hooks/useError";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { UTM, isBrowser, resolveUtmWithPriority } from '../../utils/utm';

import { PhoneNumberUtil } from 'google-libphonenumber';
const phoneUtil = PhoneNumberUtil.getInstance();

import {
  FormContainer,
  HeaderContainer,
  ButtonContainer,
  Form,
  Title,
  Text
} from "./styles";
import FontLoader, { GlobalStyles } from "../styles/global";

import { Input } from "../Input";
import { Button } from "../Button";
import { Alert } from "../Alert";
import { PhoneInput } from "../PhoneInput";

export interface MitreFormComponentProps {
  productId: string;
  apiUrl: string;
  apiToken: string;
  showHeader?: boolean;
  backgroundColor?: string;
  buttonBackgroundColor?: string;
  buttonTextColor?: string;
  textColor?: string;
  innerPadding?: string;
  inputBackgroundColor?: string;
  inputFocusBorderColor?: string;
  inputBorderColor?: string;
  inputTextColor?: string;
  inputPlaceholderColor?: string;
  onSuccess?: (requestBody: RequestBody, leadId: string) => void;
  onError?: (error: Error) => void;
}

export interface RequestBody {
  name: string;
  email: string;
  phone: string;
  message: string;
  productId: string;
  utm_source: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
}

const isPhoneValid = (phone: string) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch (error) {
    console.log('erro ao validar telefone = ' + error)
    return false;
  }
};

const schema = yup.object().shape({
  name: yup.string().required("Nome é obrigatório"),
  email: yup.string().required("Email é obrigatório").email("Email inválido"),
  phone: yup.object().shape({
    phone: yup.string().required("Telefone é obrigatório!")
      .test(
        'is-valid-phone',
        'Número de telefone inválido!',
        function (value) {
          const digitsOnly = value?.replace(/\D/g, '') || '';
          return digitsOnly.length >= 8 && isPhoneValid(value)
        }),
    inputValue: yup.string().required("Telefone é obrigatório!"),
    dialCode: yup.string().required("Código de país é obrigatório")
  }),
});

const MitreFormComponent = React.forwardRef<HTMLDivElement, MitreFormComponentProps>(({
  productId,
  apiUrl,
  apiToken,
  showHeader = true,
  backgroundColor = "var(--gray-180)",
  textColor = "var(--black)",
  buttonBackgroundColor = "#F6C76B",
  buttonTextColor = "var(--black)",
  innerPadding = "1rem",
  inputBackgroundColor = "var(--white)",
  inputBorderColor = "var(--transparent)",
  inputFocusBorderColor = "var(--yellow-500)",
  inputPlaceholderColor = "var(--gray-100)",
  inputTextColor = "var(--black)",
  onSuccess,
  onError,
}, ref) => {
  const [loading, setIsLoading] = useState(false);
  const { error, handleError, clearError } = useError();
  const [successMessage, setSuccessMessage] = useState('');
  const [formKey, setFormKey] = useState(0);

  // UTM resolved inside the component (URL > localStorage (7d TTL) > referrer)
  const [utm, setUtm] = useState<UTM>({ utm_source: 'direto', createdAt: new Date().toISOString() });

  const { control, register, handleSubmit, formState: { errors }, reset, clearErrors } = useForm({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  });

  React.useEffect(() => {
    if (!isBrowser()) return;
    const { data } = resolveUtmWithPriority(new Date());
    setUtm(data);
  }, []);

  const resetForm = () => {
    reset({
      name: "",
      email: "",
      phone: { phone: "", inputValue: "", dialCode: "" },
    }, {
      keepErrors: false,
      keepDirty: false,
      keepTouched: false,
      keepIsSubmitted: false,
      keepSubmitCount: false,
      keepValues: false,
      keepDefaultValues: false,
    });
    clearErrors();
    setFormKey(k => k + 1); // Force remount
  };

  const sendMessage: SubmitHandler<{
    name: string;
    email: string;
    phone: { phone: string; inputValue: string; dialCode: string; };
  }> = async (data) => {
    const { name, email, phone } = data;

    const phoneValue = phone.inputValue;
    const phoneDigitsOnly = phoneValue?.replace(/\D/g, '') || '';

    const message = "Gostaria de mais informações sobre o produto";

    try {
      setIsLoading(true);

      if (!productId || !apiToken) {
        throw new Error("Parâmetros obrigatórios não informados");
      }

      const requestBody: RequestBody = {
        name,
        email,
        phone: phoneDigitsOnly,
        message,
        productId,
        utm_source: utm.utm_source,
        utm_campaign: utm.utm_campaign,
        utm_medium: utm.utm_medium,
        utm_term: utm.utm_term,
      };

      const response = await fetch(`${apiUrl}/leads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${apiToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error("Falha ao enviar a mensagem!");
      }

      const responseData = await response.json();
      const leadId = responseData.leadId || responseData.id || '';

      setSuccessMessage("Mensagem enviada com sucesso!");
      resetForm();
      onSuccess?.(requestBody, leadId);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Erro desconhecido");
      handleError(err);
      onError?.(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <FontLoader />
      <GlobalStyles />

      {error && (
        <Alert
          type="error"
          dismissible
          onDismiss={clearError}
          autoDismiss={5000}
        >
          {error!.message}
        </Alert>
      )}

      {successMessage && (
        <Alert
          type="success"
          dismissible
          onDismiss={() => setSuccessMessage('')}
          autoDismiss={5000}
        >
          {successMessage}
        </Alert>
      )}

      <FormContainer $backgroundColor={backgroundColor} $innerPadding={innerPadding} ref={ref} >
        {showHeader &&
          <HeaderContainer>
            <Title $textColor={textColor}>Atendimento por mensagem</Title>

            <Text $textColor={textColor}>Informe seus dados e retornaremos a mensagem.</Text>
          </HeaderContainer>
        }

        <Form $textColor={textColor} onSubmit={handleSubmit(sendMessage)} noValidate>
          <Input
            id="name"
            label="Nome *"
            placeholder="Digite seu nome"
            {...register("name")}
            error={errors.name?.message}
            autoComplete="name"
            required
            labelTextColor={textColor}
            backgroundColor={inputBackgroundColor}
            borderColor={inputBorderColor}
            focusBorderColor={inputFocusBorderColor}
            inputPlaceholderColor={inputPlaceholderColor}
            inputTextColor={inputTextColor}
          />

          <Input
            id="email"
            label="Email *"
            type="email"
            placeholder="exemplo@email.com"
            {...register("email")}
            error={errors.email?.message}
            autoComplete="email"
            required
            labelTextColor={textColor}
            backgroundColor={inputBackgroundColor}
            borderColor={inputBorderColor}
            focusBorderColor={inputFocusBorderColor}
            inputPlaceholderColor={inputPlaceholderColor}
            inputTextColor={inputTextColor}
          />

          <Controller
            key={formKey}
            name="phone"
            control={control}
            defaultValue={{ phone: "", inputValue: "", dialCode: "" }}
            shouldUnregister={true}
            render={({ field }) => {
              const errorMsg =
                errors.phone?.inputValue?.message ||
                errors.phone?.phone?.message ||
                errors.phone?.message;

              return (
                <PhoneInput
                  id="phone"
                  label="Telefone *"
                  placeholder="(11) 00000-0000"
                  error={errorMsg}
                  required
                  value={field.value.phone}
                  onChange={field.onChange}
                  name={field.name}
                  labelTextColor={textColor}
                  backgroundColor={inputBackgroundColor}
                  borderColor={inputBorderColor}
                  focusBorderColor={inputFocusBorderColor}
                  inputPlaceholderColor={inputPlaceholderColor}
                  inputTextColor={inputTextColor}
                />
              );
            }}
          />

          <h6>* Campos de preenchimento obrigatório.</h6>

          <ButtonContainer>
            <Button bgColor={buttonBackgroundColor} color={buttonTextColor} type="submit" isSubmitting={loading}>
              Enviar mensagem
            </Button>
          </ButtonContainer>

          <p>A Mitre Realty respeita a sua privacidade e utiliza os seus dados pessoais para contatá-lo por e-mail ou telefone aqui registrados. Para saber mais, acesse a nossa{' '}
            <a
              href="https://www.mitrerealty.com.br/politica-de-privacidade"
              target="_blank"
              rel="noopener noreferrer"
            >
              Política de Privacidade
            </a>. Ao clicar em {'"'}enviar{'"'}, você concorda em permitir que a Mitre Realty, armazene e processe os dados pessoais fornecidos por você para finalidade informada</p>

        </Form>
      </FormContainer>
    </>
  );
});

MitreFormComponent.displayName = "MitreFormComponent";

export default MitreFormComponent;
